import { useState } from "react";
import { ShoppingBasket, Scissors, UtensilsCrossed, Ship, Wrench, Shirt, Car, House, Briefcase, PartyPopper, Sparkles, BadgeCheck, Star, Store, MessageSquareQuote, Tag, Award } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const ICONS = { ShoppingBasket, Scissors, UtensilsCrossed, Ship, Wrench, Shirt, Car, House, Briefcase, PartyPopper, Sparkles };
export const CategoryIcon = ({ icon, className = "w-5 h-5" }) => {
  const I = ICONS[icon] || Sparkles;
  return <I className={className} aria-hidden="true" />;
};

export const AnkaraStrip = ({ thin, className = "" }) => <div className={`ankara-strip ${thin ? "ankara-thin" : ""} ${className}`} aria-hidden="true" />;

export const Photo = ({ src, alt, icon, className = "", testid }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div data-testid={testid ? `${testid}-placeholder` : undefined} className={`flex items-center justify-center bg-[#1E4D3B] relative overflow-hidden ${className}`}>
        <div className="absolute inset-0 ankara-dots opacity-40" />
        <div className="relative w-14 h-14 rounded-full bg-[#F59E0B] text-[#291E18] flex items-center justify-center shadow-lg">
          <CategoryIcon icon={icon} className="w-7 h-7" />
        </div>
      </div>
    );
  }
  return (
    <div className={`bg-[#EFE7DC] overflow-hidden ${className}`}>
      <img data-testid={testid} src={imgUrl(src)} alt={alt} loading="lazy" decoding="async" onLoad={() => setLoaded(true)} onError={() => setFailed(true)}
        className={`w-full h-full object-cover img-fade ${loaded ? "loaded" : ""}`} />
    </div>
  );
};

const PALETTE = ["#1E4D3B", "#C2410C", "#B45309", "#7C2D12", "#166534"];
export const Avatar = ({ user, size = 40, className = "" }) => {
  const name = user?.name || "?";
  const initials = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const color = PALETTE[name.charCodeAt(0) % PALETTE.length];
  if (user?.photo_url) {
    return <img src={imgUrl(user.photo_url)} alt={name} loading="lazy" style={{ width: size, height: size }} className={`rounded-full object-cover ring-2 ring-[#FFFDF9] ${className}`} />;
  }
  return (
    <div style={{ width: size, height: size, background: color, fontSize: size * 0.38 }} className={`rounded-full flex items-center justify-center text-[#FFFDF9] font-bold ring-2 ring-[#FFFDF9] shrink-0 ${className}`} aria-label={name}>
      {initials}
    </div>
  );
};

const pill = "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap";

export const VouchPill = ({ n, testid }) => (
  <span data-testid={testid} className={`${pill} bg-[#FEF3C7] text-[#78350F] ring-1 ring-[#F59E0B]/60`}>
    <Star className="w-3 h-3 fill-[#F59E0B] text-[#D97706]" aria-hidden="true" /> {n}
  </span>
);

export const VerifiedCheck = ({ label }) => (
  <BadgeCheck className="w-4 h-4 text-[#16A34A] shrink-0" aria-label={label} role="img" />
);

export const TypeBadge = ({ type }) => {
  const { t } = useApp();
  return type === "request" ? (
    <span className={`${pill} bg-[#C2410C] text-white`}><MessageSquareQuote className="w-3 h-3" aria-hidden="true" />{t("request")}</span>
  ) : (
    <span className={`${pill} bg-[#1E4D3B] text-white`}><Tag className="w-3 h-3" aria-hidden="true" />{t("listing")}</span>
  );
};

export const ExampleBadge = () => null;

export const FeaturedBadge = ({ post }) => {
  const { t } = useApp();
  if (!post?.featured_until || post.featured_until < new Date().toISOString()) return null;
  return <span data-testid="featured-badge" className={`${pill} bg-[#F59E0B] text-[#291E18] font-extrabold shadow`}><Sparkles className="w-3 h-3" aria-hidden="true" />{t("featured")}</span>;
};

export const BuilderBadge = ({ user, dark }) => {
  const { t } = useApp();
  if (!user?.community_builder) return null;
  return <span data-testid="builder-badge" className={`${pill} ${dark ? "bg-[#FDE68A] text-[#291E18]" : "bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#F59E0B]/60"} font-bold`}><Award className="w-3 h-3" aria-hidden="true" />{t("community_builder")}</span>;
};

export const BusinessBadge = ({ business }) => {
  const { t } = useApp();
  if (!business) return null;
  const ok = business.verification_status === "approved";
  return (
    <span data-testid="business-badge" className={`${pill} ${ok ? "bg-[#1E4D3B] text-[#FDE68A]" : "bg-[#F3EDE4] text-[#1E4D3B] ring-1 ring-[#1E4D3B]/30"}`}>
      {ok ? <BadgeCheck className="w-3 h-3" aria-hidden="true" /> : <Store className="w-3 h-3" aria-hidden="true" />}
      {ok ? t("verified_business") : t("business")}
    </span>
  );
};

export const StatusBadge = ({ status }) => {
  const { t } = useApp();
  const tone = { available: "bg-[#DCFCE7] text-[#14532D]", open: "bg-[#DCFCE7] text-[#14532D]", sold: "bg-[#291E18] text-white", found: "bg-[#291E18] text-white" }[status] || "bg-[#F3EDE4] text-[#6B5E55]";
  return <span data-testid="status-badge" className={`${pill} ${tone}`}>{t(status)}</span>;
};

export const usePriceText = () => {
  const { t, lang } = useApp();
  return (p) => {
    if (p.contact_for_price) return t("contact_for_price");
    if (p.price == null) return null;
    const n = new Intl.NumberFormat(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: p.price % 1 ? 2 : 0 }).format(p.price);
    return p.type === "request" ? `${t("budget")} ${n}` : n;
  };
};

export const useDate = () => {
  const { lang } = useApp();
  return (iso, opts = { day: "numeric", month: "short" }) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", opts) : "");
};

export const useAgo = () => {
  const { lang } = useApp();
  return (iso) => {
    const rtf = new Intl.RelativeTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", { numeric: "auto" });
    const mins = Math.round((new Date(iso) - Date.now()) / 60000);
    if (Math.abs(mins) < 60) return rtf.format(mins, "minute");
    const h = Math.round(mins / 60);
    if (Math.abs(h) < 24) return rtf.format(h, "hour");
    return rtf.format(Math.round(h / 24), "day");
  };
};
